package ui.eReceipt

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.selection.DisableSelection
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.clearAndSetSemantics
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.unit.dp
import receipt.OptInTypes
import receipt.OriginType
import receipt.hasFlag
import receipt.hasFlags
import ui.Colors
import ui.eReceipt.components.DifferentCustomerBanner
import ui.eReceipt.components.Links
import ui.eReceipt.components.PerforatedEdgeContainer
import ui.eReceipt.components.QuestionSection
import ui.eReceipt.receiptTypes.DefaultReceipt
import ui.eReceipt.receiptTypes.DejavooReceipt

val eReceiptMaxWidth = 300.dp

@Composable
fun EReceiptScreen(
    viewModel: EReceiptViewModel = eReceiptViewModel()
) {
    val scrollState = rememberScrollState()
    val maxWidth = eReceiptMaxWidth

    val eReceiptStateFlow = viewModel.eReceiptStateFlow
    val isTokenAvailable = viewModel.isTokenAvailable()
    val questionStateFlow = viewModel.orderQuestionStateFlow
    val merchantInformationStateFlow = viewModel.merchantInformationStateFlow
    val consumerInformationStateFlow = viewModel.consumerInformationStateFlow

    SelectionContainer {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .fillMaxHeight()
                .background(Colors.Default)
                .padding(top = 8.dp)
                .verticalScroll(scrollState),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {

            val _eReceipt by eReceiptStateFlow.collectAsState()
            val eReceipt = _eReceipt

            if (eReceipt != null) {

                val consumerInformation by consumerInformationStateFlow.collectAsState()
                val loggedInCustomerIdMatchesReceiptCustomerId = consumerInformation?.consumer?.consumerId != eReceipt.consumerId

                if (consumerInformation != null && loggedInCustomerIdMatchesReceiptCustomerId) {
                    DifferentCustomerBanner(maxWidth)
                    Spacer(
                        modifier = Modifier
                            .height(8.dp)
                            .widthIn(0.dp, maxWidth),
                    )
                }
                PerforatedEdgeContainer(maxWidth) {
                    when {
                        eReceipt.originType.hasFlags(OriginType.Dejavoo, OriginType.SDK_API) -> {
                            DejavooReceipt(
                                maxWidth,
                                eReceipt,
                                merchantInformationStateFlow,
                                loggedInCustomerIdMatchesReceiptCustomerId
                            )
                        }

                        else -> {
                            DefaultReceipt(maxWidth, eReceipt, merchantInformationStateFlow, loggedInCustomerIdMatchesReceiptCustomerId)
                        }
                    }
                }
                DisableSelection {
                    if (!merchantInformationStateFlow.value?.optInType.hasFlag(OptInTypes.OptedOut)) {
                        QuestionSection(
                            questionStateFlow = questionStateFlow,
                            onScore = { questionId, rating ->
                                viewModel.answerQuestion(eReceipt.orderPaymentGuid, questionId, rating)
                            },
                            maxWidth
                        )
                    }
                    Links(
                        isTokenAvailable = isTokenAvailable,
                        currentCustomerIdMatchesExistingCustomerId = loggedInCustomerIdMatchesReceiptCustomerId,
                        maxWidth = maxWidth,
                        consumerOptInType = merchantInformationStateFlow.value?.optInType,
                        onCancelEnrollmentClicked = {
                            viewModel.cancelEnrollment(eReceipt.consumerLoyaltyProgramGuid.orEmpty())
                        },
                        onTermsOfServiceClicked = viewModel::setTermsOfService,
                        onPrivacyPolicyClicked = viewModel::setPrivacyPolicy,
                    ) {
                        viewModel.requestAuthenticationFromOrderPaymentGuid(eReceipt.orderPaymentGuid)
                    }
                }
            } else {
                CircularProgressIndicator(
                    modifier = Modifier
                        .align(Alignment.CenterHorizontally)
                        .clearAndSetSemantics {
                            contentDescription = "Loading eReceipt"
                        }
                )
            }
        }
    }
}